/**
 * CharacterV1 (P0-4b deliverable 4): the player-character sheet stored in `actor_sheet.data`,
 * holding the whitelisted paths of the dnd5e 5.x actor export. Frozen by CS r6a (deliverable 181)
 * from 2.G s1's freeze answers: every field the freeze added is optional (`effects`, an item's
 * `ddbId`), so a sheet stored before it parses unchanged; a later change is additive only,
 * through a contract steward round. Module flags are not part of the sheet (DECISIONS 213 ruling
 * 3): the D&D Beyond character id and URL live in `actor_sheet.external_ids`. Neither the derived
 * values (`actor_sheet.derived`) nor the macro's snapshot (`actor_sheet.foundry_snapshot`, ruling
 * 4) is part of it.
 *
 * Strings that dnd5e stores as formulas ("", "@prof", "2") stay strings.
 */
import { Ability, allDistinct, Condition, DamageBypass, DamageType, Die, FeatureId, FoundryId, Money, RulesVersion, Size, Skill, SourceMeta, Text, } from './common.js';
import { z } from './zod.js';
/** dnd5e's proficiency multipliers: none, half, proficient, expertise. */
export const PROFICIENCY_LEVELS = [0, 0.5, 1, 2];
export const Proficiency = z.literal(PROFICIENCY_LEVELS);
// ---------------------------------------------------------------- abilities
export const CharacterAbility = z.strictObject({
    score: z.number().int().min(1).max(30),
    saveProficient: z.literal([0, 1]),
    /** The score cap; null is the default. */
    max: z.number().int().min(1).nullable(),
    bonuses: z.strictObject({ check: z.string(), save: z.string() }),
});
export const CharacterAbilities = z.strictObject({
    str: CharacterAbility,
    dex: CharacterAbility,
    con: CharacterAbility,
    int: CharacterAbility,
    wis: CharacterAbility,
    cha: CharacterAbility,
});
export const ROLL_MODES = [-1, 0, 1];
export const CharacterSkill = z.strictObject({
    key: Skill,
    value: Proficiency,
    ability: Ability,
    bonuses: z.strictObject({ check: z.string(), passive: z.string() }),
    /** -1 disadvantage, 0 normal, 1 advantage. */
    rollMode: z.literal(ROLL_MODES),
});
export const CharacterTool = z.strictObject({
    /** dnd5e's tool key: "herb", "thief", "flute". */
    key: Text,
    value: Proficiency,
    ability: Ability,
    bonuses: z.strictObject({ check: z.string() }),
});
// --------------------------------------------------------------- attributes
export const CharacterAc = z.strictObject({
    /** dnd5e's calculation key: "default", "flat", "natural", "unarmoredMonk", "custom". */
    calc: Text,
    flat: z.number().int().nullable(),
    formula: z.string(),
});
export const CharacterHp = z.strictObject({
    value: z.number().int(),
    /** A non-null maximum is an override of the computed one. */
    max: z.number().int().min(0).nullable(),
    temp: z.number().int().min(0).nullable(),
    tempMax: z.number().int().nullable(),
    bonuses: z.strictObject({ level: z.string(), overall: z.string() }),
});
/** Speeds are numbers or formula strings in dnd5e 5.2; both are kept as their formula. */
export const Movement = z.strictObject({
    walk: z.string().nullable(),
    burrow: z.string().nullable(),
    climb: z.string().nullable(),
    fly: z.string().nullable(),
    swim: z.string().nullable(),
    units: z.string().nullable(),
    hover: z.boolean(),
    bonus: z.string().nullable(),
    special: z.string().nullable(),
    ignoredDifficultTerrain: z.array(z.string()),
});
export const CharacterSenses = z.strictObject({
    darkvision: z.number().min(0).nullable(),
    blindsight: z.number().min(0).nullable(),
    tremorsense: z.number().min(0).nullable(),
    truesight: z.number().min(0).nullable(),
    units: z.string().nullable(),
    special: z.string().nullable(),
});
// ------------------------------------------------------------------ traits
export const DamageTraitSet = z.strictObject({
    values: z.array(DamageType),
    bypasses: z.array(DamageBypass),
    custom: z.string(),
});
export const CharacterTraits = z.strictObject({
    size: Size,
    languages: z.strictObject({
        /** dnd5e's language keys: "common", "elvish". */
        values: z.array(Text),
        /** The custom entries, split on ";", trimmed, empties dropped. */
        custom: z.array(Text),
        communication: z.array(z.strictObject({
            key: Text,
            value: z.number().min(0).nullable(),
            units: z.string().nullable(),
        })),
    }),
    di: DamageTraitSet,
    dr: DamageTraitSet,
    dv: DamageTraitSet,
    ci: z.strictObject({ values: z.array(Condition), custom: z.string() }),
    /** Damage modification: a formula per damage type. */
    dm: z.strictObject({
        amounts: z.array(z.strictObject({ key: DamageType, formula: z.string() })),
        bypasses: z.array(DamageBypass),
    }),
    weaponProf: z.strictObject({ values: z.array(Text), custom: z.string() }),
    armorProf: z.strictObject({ values: z.array(Text), custom: z.string() }),
});
// ---------------------------------------------------------- resources, slots
export const RESOURCE_KEYS = ['primary', 'secondary', 'tertiary'];
export const Resource = z.strictObject({
    key: z.enum(RESOURCE_KEYS),
    label: z.string(),
    value: z.number().int().min(0),
    max: z.number().int().min(0),
    /** Recovers on a short rest. */
    sr: z.boolean(),
    /** Recovers on a long rest. */
    lr: z.boolean(),
});
export const SpellSlot = z.strictObject({
    level: z.number().int().min(1).max(9),
    value: z.number().int().min(0),
    /** A non-null override replaces the computed maximum. */
    override: z.number().int().min(0).nullable(),
});
export const AttackBonuses = z.strictObject({ attack: z.string(), damage: z.string() });
export const GlobalBonuses = z.strictObject({
    mwak: AttackBonuses,
    rwak: AttackBonuses,
    msak: AttackBonuses,
    rsak: AttackBonuses,
    abilities: z.strictObject({ check: z.string(), save: z.string(), skill: z.string() }),
    spell: z.strictObject({ dc: z.string() }),
});
// ------------------------------------------------------------------ details
/** A detail that is either plain text or the id of an embedded item. */
export const TextOrItem = z
    .strictObject({ text: z.string().nullable(), itemId: FoundryId.nullable() })
    .refine((v) => v.text === null || v.itemId === null, {
    message: 'Text or an item id, not both',
    path: ['itemId'],
});
export const CharacterDetails = z.strictObject({
    race: TextOrItem,
    background: TextOrItem,
    /** The id of the class item taken at level 1. */
    originalClass: FoundryId.nullable(),
    alignment: z.string(),
    xp: z.number().int().min(0),
    /** The personality fields: sanitized HTML strings. */
    trait: z.string(),
    ideal: z.string(),
    bond: z.string(),
    flaw: z.string(),
    appearance: z.strictObject({
        appearance: z.string(),
        age: z.string(),
        eyes: z.string(),
        faith: z.string(),
        gender: z.string(),
        hair: z.string(),
        height: z.string(),
        skin: z.string(),
        weight: z.string(),
    }),
    biography: z.strictObject({ value: z.string(), public: z.string() }),
});
// ------------------------------------------------------------------ classes
/** A HitPoints advancement value: "max" (the die's faces), "avg" or a rolled number. */
export const HitPointChoice = z.strictObject({
    level: z.number().int().min(1).max(20),
    value: z.union([z.literal('max'), z.literal('avg'), z.number().int().min(1)]),
});
export const CharacterClass = z.strictObject({
    itemId: FoundryId,
    name: Text,
    identifier: Text,
    levels: z.number().int().min(1).max(20),
    hitDie: Die,
    hitDiceSpent: z.number().int().min(0),
    hitDiceAdditional: z.string(),
    spellcasting: z.strictObject({
        /** "full", "half", "third", "pact", "artificer", "none". */
        progression: Text,
        ability: Ability.nullable(),
    }),
    hitPointAdvancement: z.array(HitPointChoice),
    subclass: z.strictObject({ itemId: FoundryId, name: Text, identifier: Text }).nullable(),
});
// -------------------------------------------------------------- activities
/** dnd5e 5.x's damage part: `{number}d{denomination} + bonus`, or a custom formula. */
export const FoundryDamagePart = z.strictObject({
    number: z.number().int().min(0).nullable(),
    denomination: z.number().int().min(0).nullable(),
    bonus: z.string(),
    types: z.array(Text),
    custom: z.strictObject({ enabled: z.boolean(), formula: z.string() }),
    scaling: z.strictObject({
        mode: z.enum(['', 'whole', 'half']),
        number: z.number().int().nullable(),
        formula: z.string(),
    }),
});
export const Recovery = z.strictObject({ period: Text, type: Text, formula: z.string() });
export const Uses = z.strictObject({
    /** A formula; "" is no limited uses. */
    max: z.string(),
    spent: z.number().int().min(0),
    recovery: z.array(Recovery),
});
export const ACTIVITY_TYPES = [
    'attack',
    'cast',
    'check',
    'damage',
    'enchant',
    'forward',
    'heal',
    'save',
    'summon',
    'transform',
    'utility',
];
export const Activity = z.strictObject({
    /** The activity's own id, which is also its key in `system.activities`. */
    foundryId: FoundryId,
    type: z.enum(ACTIVITY_TYPES),
    /** null uses the type's default name. */
    name: z.string().nullable(),
    sort: z.number().int(),
    activation: z.strictObject({
        type: z.string(),
        value: z.number().nullable(),
        condition: z.string(),
        override: z.boolean(),
    }),
    consumption: z.strictObject({
        targets: z.array(z.strictObject({
            type: Text,
            target: z.string(),
            value: z.string(),
            scaling: z.strictObject({ mode: z.string(), formula: z.string() }),
        })),
        scaling: z.strictObject({ allowed: z.boolean(), max: z.string() }),
        spellSlot: z.boolean(),
    }),
    duration: z.strictObject({
        value: z.string().nullable(),
        units: z.string(),
        special: z.string(),
        concentration: z.boolean(),
        override: z.boolean(),
    }),
    range: z.strictObject({
        value: z.string().nullable(),
        units: z.string(),
        special: z.string(),
        override: z.boolean(),
    }),
    target: z.strictObject({
        affects: z.strictObject({
            count: z.string(),
            type: z.string(),
            choice: z.boolean(),
            special: z.string(),
        }),
        template: z.strictObject({
            count: z.string(),
            contiguous: z.boolean(),
            type: z.string(),
            size: z.string(),
            width: z.string(),
            height: z.string(),
            units: z.string(),
        }),
        prompt: z.boolean(),
        override: z.boolean(),
    }),
    uses: Uses,
    /** The ids of the item effects the activity applies. */
    effects: z.array(z.strictObject({ id: FoundryId })),
    attack: z
        .strictObject({
        /** An ability key, "spellcasting", "none" or "" for the default. */
        ability: z.string(),
        bonus: z.string(),
        flat: z.boolean(),
        criticalThreshold: z.number().int().nullable(),
        type: z.strictObject({
            value: z.enum(['melee', 'ranged']),
            classification: z.enum(['weapon', 'spell', 'unarmed']),
        }),
    })
        .nullable(),
    damage: z
        .strictObject({
        includeBase: z.boolean().nullable(),
        /** A save's damage on a success: "half", "none". */
        onSave: z.string().nullable(),
        parts: z.array(FoundryDamagePart),
    })
        .nullable(),
    save: z
        .strictObject({
        ability: z.array(z.string()),
        dc: z.strictObject({ calculation: z.string(), formula: z.string() }),
    })
        .nullable(),
    check: z
        .strictObject({
        ability: z.string(),
        associated: z.array(z.string()),
        dc: z.strictObject({ calculation: z.string(), formula: z.string() }),
    })
        .nullable(),
    healing: FoundryDamagePart.nullable(),
    roll: z
        .strictObject({
        formula: z.string(),
        name: z.string(),
        prompt: z.boolean(),
        visible: z.boolean(),
    })
        .nullable(),
});
// ------------------------------------------------------------------- items
export const ITEM_TYPES = [
    'background',
    'class',
    'consumable',
    'container',
    'equipment',
    'feat',
    'loot',
    'race',
    'spell',
    'subclass',
    'tool',
    'weapon',
];
export const TypeValue = z.strictObject({
    value: z.string(),
    /** A weapon's or tool's base item, or a feat's or consumable's subtype. */
    detail: z.string().nullable(),
});
export const WeaponDisplay = z.strictObject({
    type: TypeValue,
    properties: z.array(Text),
    damage: z.strictObject({
        base: FoundryDamagePart.nullable(),
        versatile: FoundryDamagePart.nullable(),
    }),
    range: z.strictObject({
        value: z.number().min(0).nullable(),
        long: z.number().min(0).nullable(),
        units: z.string(),
        reach: z.number().min(0).nullable(),
    }),
    /** null is automatic. */
    proficient: z.number().nullable(),
    magicalBonus: z.number().int().nullable(),
});
export const EquipmentDisplay = z.strictObject({
    type: TypeValue,
    armor: z.strictObject({
        value: z.number().int().nullable(),
        dex: z.number().int().nullable(),
        magicalBonus: z.number().int().nullable(),
    }),
    strength: z.number().int().nullable(),
    properties: z.array(Text),
});
export const ConsumableDisplay = z.strictObject({
    type: TypeValue,
    properties: z.array(Text),
    autoDestroy: z.boolean(),
});
export const ToolDisplay = z.strictObject({
    type: TypeValue,
    ability: z.string(),
    proficient: z.number().nullable(),
});
export const ContainerDisplay = z.strictObject({
    capacity: z.strictObject({ weight: z.number().min(0).nullable(), units: z.string() }),
    /** Coins kept in the container; every key, as on the actor. */
    currency: Money,
});
export const SpellDisplay = z.strictObject({
    level: z.number().int().min(0).max(9),
    school: z.string(),
    /** "vocal", "somatic", "material", "concentration", "ritual". */
    properties: z.array(Text),
    materials: z.strictObject({
        value: z.string(),
        consumed: z.boolean(),
        cost: z.number().min(0),
        supply: z.number().int().min(0),
    }),
    activation: z.strictObject({
        type: z.string(),
        value: z.number().nullable(),
        condition: z.string(),
    }),
    range: z.strictObject({ value: z.string().nullable(), units: z.string(), special: z.string() }),
    duration: z.strictObject({ value: z.string().nullable(), units: z.string() }),
    /** "spell", "innate", "atwill", "pact", "ritual", as dnd5e 5.1's `method`. */
    method: z.string(),
    /** 0 unprepared, 1 prepared, 2 always prepared. */
    prepared: z.literal([0, 1, 2]),
    sourceClass: z.string().nullable(),
    ability: z.string().nullable(),
});
export const FeatDisplay = z.strictObject({
    type: TypeValue,
    requirements: z.string(),
    properties: z.array(Text),
});
const TYPED_BLOCKS = {
    weapon: 'weapon',
    equipment: 'equipment',
    consumable: 'consumable',
    tool: 'tool',
    container: 'containerStats',
    spell: 'spell',
    feat: 'feat',
};
export const CharacterItem = z
    .strictObject({
    id: FeatureId,
    foundryId: FoundryId,
    name: Text,
    type: z.enum(ITEM_TYPES),
    sort: z.number().int(),
    identifier: z.string(),
    /** Sanitized HTML. */
    description: z.string(),
    rules: RulesVersion.nullable(),
    quantity: z.number().int().min(0).nullable(),
    weight: z.strictObject({ value: z.number().min(0), units: z.string() }).nullable(),
    price: z.strictObject({ value: z.number().min(0), denomination: z.string() }).nullable(),
    rarity: z.string().nullable(),
    equipped: z.boolean().nullable(),
    attunement: z.enum(['required', 'optional']).nullable(),
    attuned: z.boolean().nullable(),
    /** The Foundry id of the container item holding this one. */
    containerId: FoundryId.nullable(),
    uses: Uses.nullable(),
    weapon: WeaponDisplay.nullable(),
    equipment: EquipmentDisplay.nullable(),
    consumable: ConsumableDisplay.nullable(),
    tool: ToolDisplay.nullable(),
    containerStats: ContainerDisplay.nullable(),
    spell: SpellDisplay.nullable(),
    feat: FeatDisplay.nullable(),
    activities: z.array(Activity).refine((list) => allDistinct(list.map((a) => a.foundryId)), {
        message: 'Each activity id appears once',
    }),
    /**
     * The D&D Beyond item id (`flags.ddbimporter.id`, a number written as its text): a secondary
     * match key for a refresh (DECISIONS 216 ruling 6). Optional, so a sheet stored before the
     * freeze parses; null when the item carries none. Unique among the sheet's items.
     */
    ddbId: z.string().min(1).max(64).nullable().optional(),
})
    .superRefine((item, ctx) => {
    for (const [type, block] of Object.entries(TYPED_BLOCKS)) {
        const present = item[block] !== null;
        if (present !== (item.type === type)) {
            ctx.addIssue({
                code: 'custom',
                message: `${block} is present exactly when the item type is ${type}`,
                path: [block],
            });
        }
    }
});
// ----------------------------------------------------------------- effects
/** The bounds of the effect list (CS r6a deliverable 181). */
export const CHARACTER_EFFECTS_MAX = 500;
export const EFFECT_CHANGES_MAX = 100;
/**
 * One change of an active effect, as Foundry stores it: the data path it changes, the mode
 * (0 custom, 1 multiply, 2 add, 3 downgrade, 4 upgrade, 5 override) and the value. A change whose
 * key is not dnd5e's own (`system.*`, `flags.dnd5e.*`) keeps its key with an empty value: a
 * module's value, which may hold a script, is never stored (2.G deliverable 6).
 */
export const CharacterEffectChange = z.strictObject({
    key: z.string().min(1).max(500),
    mode: z.number().int().min(0).max(5),
    value: z.string().max(2000),
});
/**
 * An active effect, raw, in 2.G deliverable 6's shape (DECISIONS 216 ruling 1): on an item of the
 * sheet (`itemFoundryId`, that item's Foundry id) or on the actor itself (null). The derive reads
 * it from the stored sheet, never from the raw asset.
 */
export const CharacterEffect = z.strictObject({
    itemFoundryId: FoundryId.nullable(),
    effectId: FoundryId,
    name: z.string().max(1024),
    /** Whether the effect transfers from its item to the actor (Foundry's default is true). */
    transfer: z.boolean(),
    disabled: z.boolean(),
    changes: z.array(CharacterEffectChange).max(EFFECT_CHANGES_MAX),
});
// ---------------------------------------------------------------- the sheet
export const Favorite = z.strictObject({
    /** "item", "effect", "skill", "tool", "slots", "activity". */
    type: Text,
    /** ".Item.<id>" for an embedded item. */
    id: Text,
    sort: z.number().int(),
});
export const SpecialTrait = z.strictObject({
    /** A dnd5e special-trait flag: "initiativeAdv", "powerfulBuild". */
    key: Text,
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const CharacterV1 = z
    .strictObject({
    schemaVersion: z.literal(1),
    source: SourceMeta,
    rules: RulesVersion.nullable(),
    name: Text,
    abilities: CharacterAbilities,
    skills: z.array(CharacterSkill).refine((list) => allDistinct(list.map((s) => s.key)), {
        message: 'Each skill appears once',
    }),
    tools: z.array(CharacterTool).refine((list) => allDistinct(list.map((t) => t.key)), {
        message: 'Each tool appears once',
    }),
    ac: CharacterAc,
    hp: CharacterHp,
    initiative: z.strictObject({ ability: Ability.nullable(), bonus: z.string() }),
    movement: Movement,
    senses: CharacterSenses,
    spellcastingAbility: Ability.nullable(),
    death: z.strictObject({
        success: z.number().int().min(0).max(3),
        failure: z.number().int().min(0).max(3),
    }),
    exhaustion: z.number().int().min(0).max(6),
    inspiration: z.boolean(),
    concentration: z.strictObject({ limit: z.number().int().min(0) }),
    attunement: z.strictObject({ max: z.number().int().min(0) }),
    traits: CharacterTraits,
    currency: Money,
    resources: z.array(Resource).refine((list) => allDistinct(list.map((r) => r.key)), {
        message: 'Each resource appears once',
    }),
    spellSlots: z.array(SpellSlot).refine((list) => allDistinct(list.map((s) => s.level)), {
        message: 'Each slot level appears once',
    }),
    pactSlots: z
        .strictObject({
        value: z.number().int().min(0),
        override: z.number().int().min(0).nullable(),
    })
        .nullable(),
    bonuses: GlobalBonuses,
    details: CharacterDetails,
    classes: z.array(CharacterClass),
    /** Keyed by their own id, in (sort, index) order. */
    items: z.array(CharacterItem),
    favorites: z.array(Favorite),
    specialTraits: z.array(SpecialTrait).refine((list) => allDistinct(list.map((t) => t.key)), {
        message: 'Each special trait appears once',
    }),
    /**
     * The active effects (CS r6a; DECISIONS 216 ruling 1), filled by 2.G s4's mappers. Optional:
     * a sheet without the list derives without effects (`effects_not_supplied`).
     */
    effects: z.array(CharacterEffect).max(CHARACTER_EFFECTS_MAX).optional(),
})
    .superRefine((sheet, ctx) => {
    const seenIds = new Set();
    const seenFoundry = new Set();
    const seenDdb = new Set();
    for (const [i, item] of sheet.items.entries()) {
        const previous = sheet.items[i - 1];
        if (previous !== undefined && item.sort < previous.sort) {
            ctx.addIssue({
                code: 'custom',
                message: 'Items are kept in (sort, index) order',
                path: ['items', i, 'sort'],
            });
        }
        if (seenIds.has(item.id)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Every item carries its own id',
                path: ['items', i, 'id'],
            });
        }
        if (seenFoundry.has(item.foundryId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Items are keyed by their own Foundry id',
                path: ['items', i, 'foundryId'],
            });
        }
        if (item.ddbId !== undefined && item.ddbId !== null) {
            if (seenDdb.has(item.ddbId)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Each D&D Beyond item id appears once',
                    path: ['items', i, 'ddbId'],
                });
            }
            seenDdb.add(item.ddbId);
        }
        seenIds.add(item.id);
        seenFoundry.add(item.foundryId);
    }
    for (const [i, cls] of sheet.classes.entries()) {
        if (!seenFoundry.has(cls.itemId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'A class names one of the sheet’s items',
                path: ['classes', i, 'itemId'],
            });
        }
    }
    const seenEffects = new Set();
    for (const [i, effect] of (sheet.effects ?? []).entries()) {
        if (seenEffects.has(effect.effectId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Each effect id appears once',
                path: ['effects', i, 'effectId'],
            });
        }
        seenEffects.add(effect.effectId);
        if (effect.itemFoundryId !== null && !seenFoundry.has(effect.itemFoundryId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'An item effect names one of the sheet’s items',
                path: ['effects', i, 'itemFoundryId'],
            });
        }
    }
});
//# sourceMappingURL=character.js.map